import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { getMenuItemById, genericErrorToast, infiniteScrollItemsFetchLimit, hasOrgPermission } from '@app/helpers';
import {
    IListPageBarEnabled,
    OrderType,
    Order,
    ITableHead,
    IPasswordServerPolicies,
    IDotMenu,
    FilterEnum,
    IActiveFilter
} from '@app/models';
import { IModalData, ModalComponent, PasslyButton, PasslyTable } from '@app/components';
import { setExceptionHandler, resetScroll, getModalSwitch } from '@app/store/actions';
import { TableCell } from '@material-ui/core';
import { passwordManagerPolicyService, basicService } from '@app/services';
import { Add } from '@material-ui/icons';
import { ManageVault, ManageSystem } from '@app/store/constants';
import { confirm } from '@app/components/Modal/confirmModal';
import PasswordServerPolicyExtraDetailsComponent from './components/passwordServerPolicyExtraDetails.component';
import { AxiosError } from 'axios';

interface ITableData {
    checked: boolean;
    checkboxDisabled: boolean;
    id: string;
    displayName: string;
    lastModified: Date;
    isDefault: boolean;
    vaultCount: number;
    onClick: Function;
}

enum TableHeadEnum {
    displayName = 'displayName',
    lastModified = 'lastModified',
    vaultCount = 'vaultCount',
    defaultSign = 'defaultSign'
}

const PasswordPolicyPage = () => {
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.PasswordPolicy);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [loadingData, setLoadingData] = useState(true);
    const [rows, setRows] = useState<ITableData[]>([]);
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [orgAllowedToManageThisPage, setOrgAllowedToManageThisPage] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState(0);
    const [resetScrollCount, setResetScrollCount] = useState(0);

    const [filters, setFilters] = useState<IActiveFilter[]>([]);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: false
    };

    const getComplexDefaultSignCell = (row: any) => {
        return <TableCell>{!row.isDefault ? <span /> : <span className='label__important'>{translation('DEFAULT')}</span>}</TableCell>;
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.displayName,
            label: 'PASSWORD_SERVER_POLICY_NAME',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.displayName,
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'PASSWORD_SERVER_POLICY_LAST_MODIFIED',
            type: 'date'
        },
        {
            id: TableHeadEnum.vaultCount,
            label: 'VAULTS'
        },
        {
            id: TableHeadEnum.defaultSign,
            label: '',
            type: 'getComplexCell',
            width: '100px',
            getComplexCell: getComplexDefaultSignCell
        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        setOrgAllowedToManageThisPage(hasOrgPermission([ManageVault, ManageSystem]));
    }, []);

    useEffect(() => {
        if (!loadingData) {
            setResetScrollCount(resetScrollCount + 1);
        }
    }, [filters]);

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        passwordManagerPolicyService.getPolicies(
            orderBy,
            order,
            infiniteScrollItemsFetchLimit,
            skipCount,
            isReset,
            handleError,
            handleSuccess,
            true,
            filters,
        );
    };

    const editPolicyClick = (id: string) => () => {
        basicService.redirect('/passwordserver/passwordserverpolicy/editpolicy/' + id);
    };

    const handleSuccess = (response: any) => {
        const count = response.data['@odata.count'];

        if (count !== undefined) {
            setTotalCount(count);
        }

        const results: IPasswordServerPolicies[] = response.data.value;

        if (results) {
            let newRows = results.map((row: IPasswordServerPolicies) => {
                const data: ITableData = {
                    checked: false,
                    checkboxDisabled: row.IsDefault,
                    id: row.Id,
                    displayName: row.DisplayName,
                    vaultCount: row.VaultCount,
                    lastModified: row.LastModified,
                    isDefault: row.IsDefault,
                    onClick: editPolicyClick(row.Id)
                };

                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setLoadingData(false);
    };

    const handleError = (error: any) => {
        setLoadingData(false);

        if (error.response.status === 401 && error.response.status === 404) {
            setOrgAllowedToManageThisPage(false);
        } else {
            genericErrorToast(error);
        }
    };

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        setOrder(ascDesc);
        setOrderBy(property);
        setLoadingData(true);
        dispatch(resetScroll());
    };

    const openDetails = (row: ITableData) => () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: row.displayName,
            modalSwitcher: modalSwitchState,
            form: <PasswordServerPolicyExtraDetailsComponent id={row.id} />
        });
    };

    const addPolicy = () => {
        basicService.redirect('/passwordserver/passwordserverpolicy/addpolicy');
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const deleteSelected = () => {
        let ids: string[] = [];
        rows.forEach((row) => {
            if (row.checked && !row.isDefault) {
                ids.push(row.id);
            }
        });

        deletePolicyDialog(ids);
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deletePolicyDialog(ids);
    };

    const deletePolicyDialog = (ids: string[]) => {
        confirm(
            translation('DELETE_PASSWORD_SERVER_POLICY_BODY'),
            translation('DELETE'),
            translation('CANCEL'),
            translation('DELETE_PASSWORD_SERVER_POLICY_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                deletePolicies(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deletePolicies = (ids: string[]) => {
        let json: any = {};

        json.PasswordServerPolicies = [];

        ids.forEach(function (id) {
            json.PasswordServerPolicies.push(id);
        });

        passwordManagerPolicyService.deletePolicy(json, handleDeleteError, handleDeleteSuccess);
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const handleDeleteError = (error: AxiosError<any>) => {
        if (error.response?.data?.error) {
            const allMessages = error.response.data.error.message.replace('||', '<br /><br />');

            confirm(allMessages, translation('OK'), '', translation('SEVERITY_WARNING'), true).then(
                () => {
                    // do nothing on success
                },
                () => {
                    // do nothing on cancel
                }
            );
        } else {
            genericErrorToast(error);
        }
    };

    return (
        <>
            <ModalComponent {...modalData} />
            {orgAllowedToManageThisPage ? (
                <>
                    <div className='passwordVaults'>
                        <div className='vaults__section-head-inputs vaults__section-head-inputs-right'>
                            {orgAllowedToManageThisPage && (
                                <PasslyButton color='primary' variant='contained' onClick={addPolicy}>
                                    <Add color='inherit' />
                                    {translation('NEW')}
                                </PasslyButton>
                            )}
                        </div>
                    </div>
                    <PasslyTable
                        rows={rows}
                        setRows={setRows}
                        setFilters={setFilters}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={totalCount}
                        onFetch={fetchMoreData}
                        withCheckboxes={orgAllowedToManageThisPage}
                        emptyListHelper={translation('EMPTY_PASSWORD_SERVER_POLICY_LIST')}
                        loadingData={loadingData}
                        openDetails={openDetails}
                        deleteRow={deleteRow}
                        resetLocal={resetScrollCount}
                        actionMenuItems={hasOrgPermission([ManageVault, ManageSystem]) ? actionMenuItems : undefined}
                    />{' '}
                </>
            ) : (
                <div className='table--empty'>{translation('NO_ORG_PASSWORD_SERVER_POLICY_PERMISSIONS')}</div>
            )}
        </>
    );
};

export default PasswordPolicyPage;
